import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function PricingSection() {
  const { ref: cardRef, isVisible: cardVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation({ threshold: 0.1 });

  const steps = [
    { number: '1', label: 'Enquire' },
    { number: '2', label: 'We Contact You' },
    { number: '3', label: 'Payment Arranged' },
    { number: '4', label: 'Access Issued' },
  ];

  return (
    <section
      id="pricing-section"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{
        paddingBottom: 'calc(var(--sticky-footer-h, 72px) + 24px + env(safe-area-inset-bottom, 0px))',
        backgroundColor: '#FDF6EE',
        backgroundImage: `radial-gradient(circle, #E48523 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }}
    >
      {/* Soft vignette overlay to fade the dots at edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 40%, #FDF6EE 100%)',
        }}
      />

      {/* Top & bottom edge fades to blend with adjacent sections */}
      <div className="absolute inset-x-0 top-0 h-20 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, #ffffff, transparent)' }} />
      <div className="absolute inset-x-0 bottom-0 h-20 pointer-events-none"
        style={{ background: 'linear-gradient(to top, #ffffff, transparent)' }} />

      <div className="max-w-3xl mx-auto px-6 lg:px-12 relative z-10">
        <div
          ref={cardRef as React.RefObject<HTMLDivElement>}
          className={`bg-white rounded-3xl shadow-xl p-6 sm:p-10 lg:p-16 border-t-4 border-[#E48523] transition-all duration-700 ease-out ${
            cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-widest text-[#BA3D03] font-semibold">
              COMPANY PRICING
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0E0503] mt-4 mb-8">
              Tailored for your organisation
            </h2>
          </div>

          <div className="space-y-3 mb-8">
            {[
              { text: <><strong>Company pricing available on request</strong></> },
              { text: '12-month access included' },
              { text: 'Access link/code provided after payment' },
              { text: 'Self-paced online delivery' },
            ].map((item, index) => (
              <div key={index} className="grid gap-3" style={{ gridTemplateColumns: '1.5rem 1fr' }}>
                <div className="flex items-center justify-center w-6 h-6 mt-0.5 flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-[#BA3D03] text-xl leading-none"></i>
                </div>
                <p className="text-lg text-[#3D1203] leading-snug">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="text-sm text-[#7A4A30] text-center mb-10 italic">
            Use the enquiry bar at the bottom of the page to request pricing directly.
          </p>

          <div className="border-t border-[#E8C580] pt-10">
            <h3 className="text-xl font-bold text-[#0E0503] text-center mb-10">What happens next</h3>
            <div
              ref={stepsRef as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4"
            >
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center relative transition-all duration-500 ease-out ${
                    stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  <div className="w-16 h-16 rounded-full bg-[#BA3D03] flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-lg">
                    {step.number}
                  </div>
                  <p className="text-sm font-semibold text-[#0E0503] text-center">{step.label}</p>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-7 -right-6">
                      <i className="ri-arrow-right-line text-2xl text-[#BA3D03] opacity-40"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
